import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetAllowedOrganizationOfferGroupsQueryKey,
  getGetAllowedOrganizationTagsQueryKey,
  useGetAllowedOrganizationOfferGroupsHook,
  useGetAllowedOrganizationTagsHook,
  useListBobcatOfferGroupsHook,
  useSetAllowedOrganizationOfferGroupsHook,
  useSetAllowedOrganizationTagsHook,
} from '@/api/trafikportalen';
import {
  offerGroupIdsAllowingProduct,
  tagIdsAfterChanges,
} from '@/helpers/periodProducts';
import type { PeriodTagChanges } from '@/helpers/periodProducts';
import type { CollectionTypeDTO } from '@/api/trafikportalen/models/collectionTypeDTO';

interface PeriodProducts {
  relational: boolean;
  stina_product_id?: string;
  tags: PeriodTagChanges;
}

/**
 * Allows a new period's tickets for every organisation it is created for.
 * Organisations that do not manage their own periods have no ticket settings,
 * so the period decides which tickets they can be issued.
 */
export const useAllowPeriodProducts = () => {
  const queryClient = useQueryClient();
  const listOfferGroups = useListBobcatOfferGroupsHook();
  const getAllowedOfferGroups = useGetAllowedOrganizationOfferGroupsHook();
  const getAllowedTags = useGetAllowedOrganizationTagsHook();
  const setAllowedOfferGroups = useSetAllowedOrganizationOfferGroupsHook();
  const setAllowedTags = useSetAllowedOrganizationTagsHook();

  return useCallback(
    async (
      organizationIds: string[],
      collectionType: CollectionTypeDTO,
      period: PeriodProducts,
    ) => {
      if (period.relational) {
        const { tagIds, removedTagIds, removableOrganizationIds } = period.tags;
        if (tagIds.length === 0 && removedTagIds.length === 0) return;
        await Promise.all(
          organizationIds.map(async (organizationId) => {
            const allowed = await getAllowedTags(organizationId);
            const nextTagIds = tagIdsAfterChanges(
              allowed,
              collectionType,
              tagIds,
              removableOrganizationIds.includes(organizationId)
                ? removedTagIds
                : [],
            );
            if (!nextTagIds) return;
            await setAllowedTags(organizationId, {
              collection_type: collectionType,
              tags: nextTagIds,
            });
            await queryClient.invalidateQueries({
              queryKey: getGetAllowedOrganizationTagsQueryKey(organizationId),
            });
          }),
        );
        return;
      }

      const stinaProductId = period.stina_product_id;
      if (!stinaProductId) return;
      const offerGroups = await listOfferGroups();
      await Promise.all(
        organizationIds.map(async (organizationId) => {
          const allowed = await getAllowedOfferGroups(organizationId);
          const offerGroupIds = offerGroupIdsAllowingProduct(
            allowed,
            offerGroups,
            collectionType,
            stinaProductId,
          );
          if (!offerGroupIds) return;
          await setAllowedOfferGroups(organizationId, {
            collection_type: collectionType,
            offers: offerGroupIds,
          });
          await queryClient.invalidateQueries({
            queryKey:
              getGetAllowedOrganizationOfferGroupsQueryKey(organizationId),
          });
        }),
      );
    },
    [
      getAllowedOfferGroups,
      getAllowedTags,
      listOfferGroups,
      queryClient,
      setAllowedOfferGroups,
      setAllowedTags,
    ],
  );
};
